import { useEffect, useState } from 'react';
import styled from 'styled-components';
import OvLoadingIndicator from '../UI/atoms/OvLoadingIndicator';
import usePaginationQueryParams from '../../hooks/use-pagination-query-params';
import { isEmpty } from 'lodash';
import { UserInfo } from '../../common/model/dto/user-info';
import OvAdminScanList from '../UI/organisms/OvAdminScanList';
import { useAdminScanMutations } from '../../hooks/use-admin-scan-mutations';
import queryString from 'query-string';
import { ScansService } from '../../services/scans.service';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import OvErrorMessage from '../UI/atoms/OvErrorMessage';
import OvNoContent from '../UI/molecules/OvNoContent';
import OvPaginationBar from '../UI/molecules/OvPaginationBar';
import useAuthorized from '../../hooks/use-authorized';
import { Resource } from '../../common/model/type/resource.enum';
import OvButton from '../UI/atoms/OvButton';
import Variables from '../../design-system/variables';
import OvCreateScanDialog from '../UI/molecules/OvCreateScanDialog';
import DateUtils from '../../common/utils/services/date-utils';
import { CreateScanRequest } from '../../common/model/dto/create-scan-request';
import { useTranslation } from 'react-i18next';
import AddCircleIcon from '@mui/icons-material/AddCircle';

const SCAN_LIST_LIMIT = 10;

const UserScanList = ({ selectedUser }: { selectedUser: UserInfo }) => {
  const { t } = useTranslation();
  const [isCreateScanDialogOpen, setIsCreateScanDialogOpen] =
    useState<boolean>(false);
  const { offset, orderBy, orderDir, setOffset, setOrderBy, setOrderDir } =
    usePaginationQueryParams();

  const applicationOperations = useAuthorized(Resource.Application);
  const scanOperations = useAuthorized(Resource.Scan);

  const scanCreateAllowed =
    scanOperations.create || applicationOperations.supervise;

  const query = queryString.stringify({
    limit: SCAN_LIST_LIMIT,
    offset,
    order_by: isEmpty(orderBy) ? undefined : orderBy,
    order_dir: isEmpty(orderDir) ? undefined : orderDir,
    user_id: selectedUser.id,
  });

  const {
    data: paginatedScans,
    isPending: isGetScansPending,
    isFetching: isGetScansFetching,
    isLoading: isGetScanLoading,
    error: getScansError,
  } = useQuery({
    queryKey: ['admin-scans-user', query],
    queryFn: () => ScansService.loadScans(query),
    retry: 3,
    enabled: !!orderBy && !!orderDir && !!selectedUser.id,
    placeholderData: keepPreviousData,
  });

  const scans = paginatedScans?.docs ?? [];
  const total = paginatedScans?.total ?? 0;

  const { deleteScan, createScan } = useAdminScanMutations();

  const onDeleteScan = async (scanId: string) => {
    await deleteScan.mutateAsync(scanId);
  };

  const onSaveScan = async (request: CreateScanRequest) => {
    setIsCreateScanDialogOpen(false);
    await createScan.mutateAsync(request);
  };

  const goToPage = (_: any, value: number) => {
    setOffset((value - 1) * SCAN_LIST_LIMIT);
  };

  useEffect(() => {
    setOrderBy('day');
    setOrderDir('desc');
  }, [setOrderBy, setOrderDir]);

  const loading =
    isGetScansPending ||
    isGetScansFetching ||
    isGetScanLoading ||
    deleteScan.isPending ||
    createScan.isPending;

  return (
    <Container>
      <CenterPane>
        <ScanActions>
          <OvPaginationBar
            total={total}
            limit={SCAN_LIST_LIMIT}
            offset={offset}
            onChange={goToPage}
          />
          {scanCreateAllowed && (
            <StyledOvButton
              onClick={() => {
                setIsCreateScanDialogOpen(true);
              }}
            >
              <AddCircleIcon style={{ marginRight: '5' }} />
              {t('common.actions.create')}
            </StyledOvButton>
          )}
        </ScanActions>

        {getScansError && <OvErrorMessage message={getScansError.message} />}

        {scans.length > 0 && (
          <OvAdminScanList scans={scans} deleteScanCallback={onDeleteScan} />
        )}
        {scans.length === 0 && !loading && (
          <OvNoContent>{t('common.empty')}</OvNoContent>
        )}

        {loading && <OvLoadingIndicator position="fixed" />}

        {scanCreateAllowed && (
          <OvCreateScanDialog
            isOpen={isCreateScanDialogOpen}
            isScanLoading={createScan.isPending}
            onCancel={() => setIsCreateScanDialogOpen(false)}
            onSave={onSaveScan}
            selectedUserId={selectedUser.id}
            selectedDay={DateUtils.getDbDateTag(new Date())}
            showDaySelector={true}
            showUserSelector={false}
            showImageUploader={true}
          />
        )}
      </CenterPane>
    </Container>
  );
};

export default UserScanList;

const Container = styled.section`
  display: flex;
`;

const CenterPane = styled.div`
  flex-grow: 1;
  margin: 0 auto;
  padding: 0 1rem;
`;

const ScanActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const StyledOvButton = styled(OvButton)`
  && {
    display: flex;
    align-items: center;
    text-transform: capitalize;
    padding: 0.125rem 0.7rem 0.125rem 0.35rem;
    border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
    margin-right: 0.5rem;
    &:last-child {
      margin-right: 0;
    }
  }
`;
